import * as React from 'react';
import { ls } from '../../i18n/translations';
import {
  capitalize,
  dayjs,
  getAlarmMetadata,
  getEventType,
  skipAlarmKey,
  skipEventKey,
} from '../../helpers/utils';
import { defaultMUIDataTableOptions } from '../../helpers/datatableUtils';
import MUIDataTable, { MUIDataTableColumnDef, MUIDataTableOptions } from 'mui-datatables';
import { AlarmHistory, MachineType, Module } from '../../graphql/codegen/graphql-react';
import { AlarmsType } from '../../graphql/dataTypes';
import type { Dayjs } from 'dayjs';
import { formatHistoryDateRangeForFilename } from '../../helpers/historyDateRangeStore';
import Flex from '../ui/Flex';
import { Box } from '@mui/material';
import { colors } from '../../theme';
import { Body2 } from '../ui/Typography';

interface DeviceAlarmsTableProps {
  module: Module;
  data: AlarmHistory[];
  /** When set, CSV name includes this history range (`from_to` dates). */
  historyRangeFrom?: Dayjs | null;
  historyRangeTo?: Dayjs | null;
}

const options: MUIDataTableOptions = {
  ...defaultMUIDataTableOptions,
};

const DeviceAlarmsTable: React.FC<DeviceAlarmsTableProps> = ({
  data,
  module,
  historyRangeFrom,
  historyRangeTo,
}) => {
  const downloadSuffix =
    formatHistoryDateRangeForFilename(historyRangeFrom, historyRangeTo) ??
    dayjs().format('YYYY-MM-DD_HH_mm');

  const tableDevices = (
    [...(data || [])].map((al, i) => {
      const alarms = al.alarms as AlarmsType;
      const activeAlarms: string[] = [];
      let alarmsCount = 0;
      let eventsCount = 0;
      if (alarms) {
        const alarmsKeys = Object.keys(alarms);
        if (alarmsKeys.length === 0) {
          return null;
        } else {
          for (let i = 0; i < alarmsKeys.length; i++) {
            const alarmsKey = alarmsKeys[i];
            const eventType = getEventType(alarmsKey);
            const skipAlarm = skipAlarmKey(alarmsKey, module);
            // fix only for Cycleau and key 'onOffKey'
            // fix only for NovaNsp and key 'stop'
            // fix only for Ef315 and key 'stop'
            // see file 'Stati' on gdrive
            let checkOpposite = false;
            if (module.type === MachineType.Cycleau) {
              if (alarmsKey === 'onOffKey') {
                checkOpposite = true;
              }
            }
            if (module.type === MachineType.NovaNsp) {
              if (alarmsKey === 'stop') {
                checkOpposite = true;
              }
            }
            if (module.type === MachineType.Ef315) {
              if (alarmsKey === 'stop') {
                checkOpposite = true;
              }
            }
            if (
              !skipAlarm &&
              (checkOpposite
                ? // @ts-ignore
                  !alarms[alarmsKey]
                : // @ts-ignore
                  alarms[alarmsKey])
            ) {
              activeAlarms.push(alarmsKey);
              if (eventType === 'alarm' || eventType === 'warn') {
                alarmsCount++;
              }
            } else {
              // add 'falsy' value if the element is a 'event' and not 'alarm'
              const skipEvent = skipEventKey(alarmsKey, module);
              if (eventType === 'event' && !skipEvent) {
                activeAlarms.push(alarmsKey);
                eventsCount++;
              }
            }
          }
        }
      }
      return {
        dateAndTime: al.createdAt ? dayjs(al.createdAt).format('YYYY-MM-DD HH:mm') : null,
        data: {
          keys: activeAlarms,
          alarmsCount,
          eventsCount,
        },
      };
    }) || []
  ).filter((el) => el) as {
    dateAndTime: string;
    data: {
      keys: string[];
      alarmsCount: number;
      eventsCount: number;
    };
  }[];

  const columns: MUIDataTableColumnDef[] = [
    {
      name: 'dateAndTime',
      label: ls.dateAndTime.toUpperCase(),
      options: {
        filter: false,
        sort: true,
      },
    },
    {
      name: 'data',
      label: ls.events.toUpperCase(),
      options: {
        filter: true,
        sort: true,
        // eslint-disable-next-line react/display-name
        customBodyRender: (
          value: {
            keys: string[];
            alarmsCount: number;
            eventsCount: number;
          },
          tableMeta,
          updateValue,
        ) => {
          let OutputComp: React.ReactNode = null;
          let addAlarmsFixed = false;
          if (
            tableDevices?.[tableMeta.rowIndex + 1]?.data?.alarmsCount > 0 &&
            value.alarmsCount === 0
          ) {
            addAlarmsFixed = true;
          }
          const valueKeys = value.keys.slice();
          if (addAlarmsFixed) {
            valueKeys.push(capitalize(ls.alarmsFixed));
          }
          return (
            <Flex justifyContent='flex-start' flexWrap='wrap'>
              {valueKeys.map((v, i) => {
                if (v === capitalize(ls.alarmsFixed)) {
                  return (
                    <Box bgcolor={colors.green} borderRadius={100} py={0.5} px={2} mr={1} mt={0.5}>
                      <Body2
                        style={{
                          color: '#fff',
                        }}
                      >
                        {capitalize(ls.alarmsFixed)}
                      </Body2>
                    </Box>
                  );
                }
                let value = ls.getString('alarm_new_' + v) || ls.getString('alarm_' + v) || v;
                const alarms = (data[tableMeta.rowIndex]?.alarms || {}) as AlarmsType;
                // @ts-ignore
                const alarmValue = alarms?.[v];
                let eventType = getEventType(v);
                if (module.type && module.modelVersion) {
                  const amd = getAlarmMetadata(v, module.type, module.modelVersion);
                  if (amd) {
                    value = amd.translatedName;
                    if (ls.toolStatus.toLowerCase() === amd.translatedName.toLowerCase()) {
                      if (
                        module.type === MachineType.Ew2 ||
                        module.type === MachineType.Ew2Pool ||
                        module.type === MachineType.Nova2Pool ||
                        module.type === MachineType.Nova2PoolP ||
                        module.type === MachineType.Ew
                      ) {
                        let onOffKeyValue = alarms?.['onOffKeyValue'];
                        if (
                          module.type === MachineType.Ew2 ||
                          module.type === MachineType.Ew ||
                          module.type === MachineType.Ew2Pool ||
                          module.type === MachineType.Nova2Pool ||
                          module.type === MachineType.Nova2PoolP
                        ) {
                          // the value on ew2/ew/ew2pool/nova2pool/nova2poolp is from the first 2 bits (we maintain the same behavior)
                          if (onOffKeyValue === 0) {
                            onOffKeyValue = 256;
                          } else if (onOffKeyValue === 1) {
                            onOffKeyValue = 257;
                          } else if (onOffKeyValue === 2) {
                            onOffKeyValue = 14850;
                          }
                        }
                        if (onOffKeyValue === 257) {
                          value = amd.translatedName + ` (${ls.on.toLowerCase()})`;
                        } else if (onOffKeyValue === 14850 || onOffKeyValue === 15362) {
                          value = amd.translatedName + ` (${ls.alarm.toLowerCase()})`;
                          eventType = 'alarm';
                        } else {
                          value = amd.translatedName + ` (${ls.off.toLowerCase()})`;
                        }
                      }
                    }
                  }
                }
                return (
                  <Box
                    key={i}
                    bgcolor={
                      eventType === 'event'
                        ? colors.blue
                        : eventType === 'warn'
                        ? colors.orange
                        : colors.red
                    }
                    borderRadius={100}
                    py={0.5}
                    px={2}
                    mr={1}
                    mt={0.5}
                  >
                    <Body2
                      style={{
                        color: '#fff',
                      }}
                    >
                      {value}
                    </Body2>
                  </Box>
                );
              })}
            </Flex>
          );
        },
      },
    },
  ];

  return (
    <MUIDataTable
      title=''
      data={tableDevices}
      columns={columns}
      options={{
        ...options,
        downloadOptions: {
          filename: `DeviceEvents_${downloadSuffix}_${Date.now()}.csv`,
        },
        rowsPerPage: localStorage.getItem('alarms_rowsPerPage')
          ? Number(localStorage.getItem('alarms_rowsPerPage'))
          : 10,
        onChangeRowsPerPage: (numberOfRows) => {
          localStorage.setItem('alarms_rowsPerPage', `${numberOfRows}`);
        },
      }}
    />
  );
};

export default DeviceAlarmsTable;
